import React from 'react';

interface RegionDecision {
  region: string;
  framework: string;
  status: number;
  fallback: boolean;
}

type Status = 'done' | 'pending' | 'warn';

type Props = {
  siteKey: string;
  regionDecision: RegionDecision | null;
  regionError: string | null;
  regionLoading: boolean;
};

const badgeClassByStatus: Record<Status, string> = {
  done: 'bg-emerald-100 text-emerald-700',
  pending: 'bg-slate-100 text-slate-600',
  warn: 'bg-amber-100 text-amber-700',
};

const statusLabel: Record<Status, string> = {
  done: 'Done',
  pending: 'Pending',
  warn: 'Check',
};

const StatusBadge = ({ status }: { status: Status }) => (
  <span className={`ml-2 rounded px-1.5 py-0.5 text-xs font-medium ${badgeClassByStatus[status]}`}>
    {statusLabel[status]}
  </span>
);

export function FirstRunChecklist({ siteKey, regionDecision, regionError, regionLoading }: Props) {
  const snippetInstalled = (() => {
    if (typeof window === 'undefined') return false;
    const w = window as unknown as {
      __cmpRegion?: string;
      __cmpFramework?: string;
      __gpp?: unknown;
      __tcfapi?: unknown;
    };
    return Boolean(
      w.__cmpRegion || w.__cmpFramework || w.__gpp || typeof w.__tcfapi === 'function',
    );
  })();

  const snippetStatus: Status = snippetInstalled ? 'done' : 'pending';

  let regionStatus: Status = 'pending';
  let regionNote: string | null = null;
  if (regionLoading) {
    regionStatus = 'pending';
  } else if (regionDecision) {
    regionStatus = regionDecision.fallback ? 'warn' : 'done';
    regionNote = regionDecision.fallback
      ? 'Fallback to GDPR (404). Ensure ingress forwards client IP headers.'
      : `Resolved to ${regionDecision.framework.toUpperCase()} (${regionDecision.region}).`;
  } else if (regionError) {
    regionStatus = 'warn';
    regionNote = regionError;
  }

  const [copyStatus, setCopyStatus] = React.useState<'idle' | 'done' | 'error'>('idle');

  const handleCopySnippet = React.useCallback(() => {
    const snippet =
      '<script src="https://YOUR-CMP-CDN/dw-cmp.min.js" data-site-key="' +
      (siteKey || 'YOUR_SITE_KEY') +
      '" defer></script>';
    try {
      void navigator.clipboard.writeText(snippet);
      setCopyStatus('done');
    } catch {
      setCopyStatus('error');
    }
  }, [siteKey]);

  const openDiagnostics = React.useCallback(() => {
    if (typeof document === 'undefined') return;
    document
      .getElementById('region-decision-card')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleScrollToScans = React.useCallback(() => {
    if (typeof document === 'undefined') return;
    const el = document.getElementById('scans-card');
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return (
    <div className="rounded-lg border p-4 text-sm">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold">First-run checklist</h2>
        <span className="text-xs text-gray-500">Site: {siteKey || 'unknown'}</span>
      </div>
      <ol className="list-decimal list-inside space-y-3">
        <li>
          <div className="flex flex-col">
            <span className="font-medium">
              Install CMP snippet
              <StatusBadge status={snippetStatus} />
            </span>
            <span className="text-xs text-gray-600">
              Embed the CMP SDK on your site and confirm the banner renders locally.
            </span>
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                className="px-2 py-1 border rounded text-xs"
                onClick={handleCopySnippet}
              >
                Copy snippet
              </button>
              {copyStatus === 'done' && <span className="text-xs text-emerald-600">Copied!</span>}
              {copyStatus === 'error' && (
                <span className="text-xs text-amber-600">Clipboard unavailable—copy manually.</span>
              )}
            </div>
            {!snippetInstalled && (
              <span className="text-xs text-amber-600 mt-1">
                No CMP framework detected yet. Ensure the install snippet runs before other tags.
              </span>
            )}
          </div>
        </li>
        <li>
          <div className="flex flex-col">
            <span className="font-medium">
              Verify Region API decision
              <StatusBadge status={regionStatus} />
            </span>
            <span className="text-xs text-gray-600">
              `/api/v1/region` should return the active framework (TCF for EU/UK, GPP for US).
            </span>
            {regionNote && <span className="text-xs text-amber-600">{regionNote}</span>}
            <div className="mt-2">
              <button
                type="button"
                className="px-2 py-1 border rounded text-xs"
                onClick={openDiagnostics}
              >
                Open diagnostics
              </button>
            </div>
          </div>
        </li>
        <li>
          <div className="flex flex-col">
            <span className="font-medium">
              Run scanner baseline
              <StatusBadge status={'pending'} />
            </span>
            <span className="text-xs text-gray-600">
              Kick off a scan to capture pre-consent behaviour and validate cookie controls.
            </span>
            <button
              type="button"
              className="mt-1 w-fit px-2 py-1 border rounded text-xs"
              onClick={handleScrollToScans}
            >
              Run scanner
            </button>
          </div>
        </li>
      </ol>
      <p className="mt-3 text-xs text-gray-600">
        Auto-region is on by default. If location can’t be determined, we apply a GDPR-safe fallback
        so you remain compliant.
      </p>
    </div>
  );
}
